import React, { useState, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { storage } from '../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const FIELD_GROUPS = [
  {
    label: 'Contact Info',
    fields: [
      { key: 'name',     label: 'Full Name',       type: 'text',     required: true  },
      { key: 'phone',    label: 'Phone Number',     type: 'tel',      required: true  },
      { key: 'email',    label: 'Email Address',    type: 'email',    required: false },
      { key: 'whatsapp', label: 'WhatsApp Number',  type: 'tel',      required: false },
      { key: 'city',     label: 'City',             type: 'text',     required: false },
      { key: 'state',    label: 'State / Province', type: 'text',     required: false },
      { key: 'address',  label: 'Address',          type: 'textarea', required: false },
    ],
  },
  {
    label: 'Study Preferences',
    fields: [
      { key: 'country',       label: 'Target Country',        type: 'text',     required: false },
      { key: 'course',        label: 'Course / Program',      type: 'text',     required: false },
      { key: 'intake',        label: 'Preferred Intake',      type: 'select',   required: false },
      { key: 'qualification', label: 'Highest Qualification', type: 'text',     required: false },
      { key: 'budget',        label: 'Budget',                type: 'number',   required: false },
      { key: 'message',       label: 'Message / Notes',       type: 'textarea', required: false },
    ],
  },
  {
    label: 'Academic',
    fields: [
      { key: 'school',         label: 'School / College Name', type: 'text', required: false },
      { key: 'grade',          label: 'Grade / Class',         type: 'text', required: false },
      { key: 'board',          label: 'Board / Curriculum',    type: 'text', required: false },
      { key: 'graduationYear', label: 'Graduation Year',       type: 'text', required: false },
    ],
  },
  {
    label: 'Personal Details',
    fields: [
      { key: 'dob',         label: 'Date of Birth',   type: 'date',   required: false },
      { key: 'gender',      label: 'Gender',           type: 'select', required: false, options: ['Male', 'Female', 'Other', 'Prefer not to say'] },
      { key: 'nationality', label: 'Nationality',      type: 'text',   required: false },
      { key: 'passport',    label: 'Passport Number',  type: 'text',   required: false },
    ],
  },
  {
    label: 'Test Scores',
    fields: [
      { key: 'ielts', label: 'IELTS Score', type: 'number', required: false },
      { key: 'toefl', label: 'TOEFL Score', type: 'number', required: false },
      { key: 'pte',   label: 'PTE Score',   type: 'number', required: false },
      { key: 'gre',   label: 'GRE Score',   type: 'number', required: false },
      { key: 'gmat',  label: 'GMAT Score',  type: 'number', required: false },
      { key: 'sat',   label: 'SAT Score',   type: 'number', required: false },
      { key: 'act',   label: 'ACT Score',   type: 'number', required: false },
      { key: 'gpa',   label: 'GPA',         type: 'number', required: false },
    ],
  },
  {
    label: 'Parents / Guardian',
    fields: [
      { key: 'parentName',     label: 'Parent / Guardian Name',  type: 'text',  required: false },
      { key: 'parentPhone',    label: 'Parent / Guardian Phone', type: 'tel',   required: false },
      { key: 'parentEmail',    label: 'Parent / Guardian Email', type: 'email', required: false },
      { key: 'parentRelation', label: 'Relation to Student',     type: 'text',  required: false },
    ],
  },
];

// Flat list derived from groups — used for "all added" check and backward-compat
const AVAILABLE_FIELDS = FIELD_GROUPS.flatMap(g => g.fields);

const ACCENT_COLORS = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ef4444','#ec4899','#0f172a','#06b6d4'];

const DEFAULT_FORM = {
  name:          '',
  source:        'web_form',
  title:         'Get Free Counselling',
  subtitle:      'Fill in your details and our expert counsellors will get back to you shortly.',
  logoUrl:       '',
  submitBtnText: 'Submit Enquiry',
  thankYouTitle: 'Thank You!',
  thankYouMsg:   "We've received your enquiry and will get back to you within 24 hours.",
  redirectUrl:   '',
  accentColor:   '#7c3aed',
  privacyText:   'By submitting this form you agree to our privacy policy.',
  privacyUrl:    '',
  whatsappOptIn: false,
  whatsappOptInText: 'I agree to receive updates via WhatsApp',
  fields: [
    { key: 'name',    label: 'Full Name',      required: true,  enabled: true },
    { key: 'phone',   label: 'Phone Number',   required: true,  enabled: true },
    { key: 'email',   label: 'Email Address',  required: false, enabled: true },
    { key: 'country', label: 'Target Country', required: false, enabled: true },
    { key: 'course',  label: 'Course / Program', required: false, enabled: true },
  ],
};

function getPublicURL(formId) {
  return window.location.origin + window.location.pathname + '?form=' + formId;
}

function getEmbedCode(formId) {
  const url = getPublicURL(formId);
  return '<iframe src="' + url + '" width="100%" height="700" frameborder="0" style="border:none;border-radius:12px;box-shadow:0 4px 24px rgba(0,0,0,0.08)"></iframe>';
}

// ── Form Builder Modal ───────────────────────────────────────────────────────
function FormBuilderModal({ editForm, onClose }) {
  const { addWebForm, updateWebForm, currentUser, customFields, addCustomField } = useCRM();
  const [form, setForm] = useState(() => editForm ? {
    name:            editForm.name          || '',
    source:          editForm.source        || 'web_form',
    title:           editForm.title         || '',
    subtitle:        editForm.subtitle      || '',
    logoUrl:         editForm.logoUrl       || '',
    submitBtnText:   editForm.submitBtnText || 'Submit Enquiry',
    thankYouTitle:   editForm.thankYouTitle || 'Thank You!',
    thankYouMsg:     editForm.thankYouMsg   || '',
    redirectUrl:     editForm.redirectUrl   || '',
    accentColor:     editForm.accentColor   || '#7c3aed',
    privacyText:     editForm.privacyText   || 'By submitting this form you agree to our privacy policy.',
    privacyUrl:      editForm.privacyUrl    || '',
    whatsappOptIn:   editForm.whatsappOptIn || false,
    whatsappOptInText: editForm.whatsappOptInText || 'I agree to receive updates via WhatsApp',
    visibility:      editForm.visibility    || 'team',
    fields:          editForm.fields        || DEFAULT_FORM.fields,
  } : { ...DEFAULT_FORM, visibility: 'team' });

  const [tab, setTab]       = useState('design');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showNewField, setShowNewField] = useState(false);
  const [newField, setNewField]         = useState({ label: '', type: 'text', options: '' });
  const [savingNewField, setSavingNewField] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const updateField = (key, prop, val) =>
    setForm(p => ({ ...p, fields: p.fields.map(f => f.key === key ? { ...f, [prop]: val } : f) }));

  const addField = (af) => {
    if (form.fields.find(f => f.key === af.key)) return;
    setForm(p => ({ ...p, fields: [...p.fields, {
      key:      af.key,
      label:    af.label,
      type:     af.type    || 'text',
      options:  af.options || [],
      required: af.required || false,
      enabled:  true,
    }] }));
  };

  const removeField = (key) => {
    if (key === 'name' || key === 'phone') return;
    setForm(p => ({ ...p, fields: p.fields.filter(f => f.key !== key) }));
  };

  const moveField = (index, dir) => {
    const arr = [...form.fields];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setForm(p => ({ ...p, fields: arr }));
  };

  const handleCreateField = async () => {
    if (!newField.label.trim()) return;
    setSavingNewField(true);
    try {
      const options = newField.type === 'select' && newField.options
        ? newField.options.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const docRef = await addCustomField({ label: newField.label.trim(), type: newField.type, options });
      // Immediately add to the active form
      addField({ key: 'cf_' + docRef.id, label: newField.label.trim(), type: newField.type, options, required: false });
      setNewField({ label: '', type: 'text', options: '' });
      setShowNewField(false);
    } catch (e) {
      console.error('Failed to create field', e);
    } finally {
      setSavingNewField(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setSaveError('');
    try {
      if (editForm) await updateWebForm(editForm.id, form);
      else          await addWebForm({ ...form, createdById: currentUser?.id, createdByName: currentUser?.name });
      onClose();
    } catch (e) {
      if (e.code === 'permission-denied') {
        setSaveError('Permission denied — deploy Firestore rules first: firebase deploy --only firestore:rules');
      } else {
        setSaveError('Save failed: ' + (e.message || 'Unknown error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setTab(id)} style={{ fontSize: 13, fontWeight: 500, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', border: 'none', background: tab === id ? '#7c3aed' : 'transparent', color: tab === id ? '#fff' : '#64748b' }}>
      {label}
    </button>
  );

  return (
    <div className="modal-overlay" onClick={e => !saving && e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 820, width: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{editForm ? 'Edit Form' : 'Create Web Form'}</h2>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Customise the lead capture form for your website</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ marginBottom: 16, flexShrink: 0 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Form Name *</label>
          <input className="crm-input" placeholder="e.g. Homepage Contact Form" value={form.name} onChange={e => set('name', e.target.value)} style={{ fontSize: 14, maxWidth: 400 }} />
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(0,0,0,0.04)', padding: 4, borderRadius: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          {tabBtn('design',   '🎨 Design')}
          {tabBtn('fields',   '📝 Fields')}
          {tabBtn('messages', '💬 Messages')}
          {tabBtn('advanced', '⚙️ Advanced')}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>

          {/* ── Design tab ── */}
          {tab === 'design' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Form Headline</label>
                  <input className="crm-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Get Free Counselling" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Lead Source Tag</label>
                  <input className="crm-input" value={form.source} onChange={e => set('source', e.target.value.toLowerCase().replace(/\s+/g,'_'))} placeholder="web_form" />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Leads from this form will have this source label.</div>
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Subheading / Description</label>
                <textarea className="crm-input" value={form.subtitle} onChange={e => set('subtitle', e.target.value)} rows={2} style={{ resize: 'vertical', width: '100%' }} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Logo <span style={{ color: '#94a3b8', fontSize: 11 }}>(shown at top of form)</span></label>
                <ImageUpload value={form.logoUrl} onChange={url => set('logoUrl', url)} folder="form-logos" />
              </div>

              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Submit Button Text</label>
                <input className="crm-input" value={form.submitBtnText} onChange={e => set('submitBtnText', e.target.value)} placeholder="Submit Enquiry" />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 8 }}>Accent Colour</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {ACCENT_COLORS.map(c => (
                    <div key={c} onClick={() => set('accentColor', c)} style={{ width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer', border: form.accentColor === c ? '3px solid #0f172a' : '3px solid transparent', transition: 'border 0.1s' }} />
                  ))}
                  <input type="color" value={form.accentColor} onChange={e => set('accentColor', e.target.value)} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', padding: 0, cursor: 'pointer', background: 'none' }} title="Custom colour" />
                  <input className="crm-input" value={form.accentColor} onChange={e => set('accentColor', e.target.value)} style={{ width: 100, height: 32 }} placeholder="#7c3aed" />
                </div>
              </div>

              {/* Live preview */}
              <div style={{ gridColumn: '1 / -1', background: 'rgba(0,0,0,0.03)', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preview</div>
                <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', maxWidth: 380 }}>
                  {form.logoUrl && <img src={form.logoUrl} alt="logo" style={{ height: 36, objectFit: 'contain', marginBottom: 14, display: 'block' }} />}
                  <div style={{ width: 32, height: 4, borderRadius: 2, background: form.accentColor, marginBottom: 12 }} />
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{form.title || 'Form Title'}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>{form.subtitle}</div>
                  {form.fields.filter(f => f.enabled).slice(0, 3).map(f => (
                    <div key={f.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{f.label}{f.required && <span style={{ color: form.accentColor, marginLeft: 2 }}>*</span>}</div>
                      <div style={{ height: 32, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 7, background: '#f9fafb' }} />
                    </div>
                  ))}
                  <div style={{ height: 38, borderRadius: 9, background: form.accentColor, marginTop: 8 }} />
                  {form.privacyText && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10, textAlign: 'center' }}>{form.privacyText}</div>}
                </div>
              </div>
            </div>
          )}

          {/* ── Fields tab ── */}
          {tab === 'fields' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>Active Fields</div>
                {form.fields.map((f, i) => (
                  <div key={f.key} style={{ padding: '10px 12px', background: f.enabled ? 'rgba(124,58,237,0.04)' : 'rgba(0,0,0,0.03)', border: '1px solid ' + (f.enabled ? 'rgba(124,58,237,0.2)' : 'rgba(0,0,0,0.06)'), borderRadius: 9, marginBottom: 7, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div onClick={() => updateField(f.key, 'enabled', !f.enabled)} style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', background: f.enabled ? form.accentColor : 'rgba(0,0,0,0.12)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: f.enabled ? 17 : 3, transition: 'left 0.2s' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input className="crm-input" value={f.label} onChange={e => updateField(f.key, 'label', e.target.value)} style={{ height: 28, fontSize: 12, padding: '0 8px' }} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b', cursor: 'pointer', flexShrink: 0 }}>
                      <input type="checkbox" checked={f.required} onChange={e => updateField(f.key, 'required', e.target.checked)} disabled={f.key === 'name' || f.key === 'phone'} /> Req
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button onClick={() => moveField(i, -1)} disabled={i === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 10, lineHeight: 1, padding: 1 }}>▲</button>
                      <button onClick={() => moveField(i, 1)} disabled={i === form.fields.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 10, lineHeight: 1, padding: 1 }}>▼</button>
                    </div>
                    {f.key !== 'name' && f.key !== 'phone' && (
                      <button onClick={() => removeField(f.key)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>Add More Fields</div>

                {/* ── Grouped standard fields ── */}
                {FIELD_GROUPS.map(group => {
                  const available = group.fields.filter(af => !form.fields.find(f => f.key === af.key));
                  if (!available.length) return null;
                  return (
                    <div key={group.label}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, margin: '10px 0 5px' }}>{group.label}</div>
                      {available.map(af => (
                        <div key={af.key} onClick={() => addField(af)}
                          style={{ padding: '9px 12px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 9, marginBottom: 6, cursor: 'pointer', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}>
                          <span style={{ color: '#7c3aed', fontSize: 16 }}>+</span> {af.label}
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>{af.type}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* ── Custom fields (company-defined) ── */}
                {(customFields || []).length > 0 && (() => {
                  const customAvail = (customFields || []).filter(cf => !form.fields.find(f => f.key === 'cf_' + cf.id));
                  if (!customAvail.length) return null;
                  return (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, margin: '12px 0 6px' }}>Custom Fields</div>
                      {customAvail.map(cf => (
                        <div key={cf.id}
                          onClick={() => addField({ key: 'cf_' + cf.id, label: cf.label, type: cf.type || 'text', options: cf.options || [], required: false })}
                          style={{ padding: '9px 12px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 9, marginBottom: 6, cursor: 'pointer', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}>
                          <span style={{ color: '#10b981', fontSize: 16 }}>+</span> {cf.label}
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>{cf.type || 'text'}</span>
                        </div>
                      ))}
                    </>
                  );
                })()}

                {/* ── Create new custom field ── */}
                <div style={{ marginTop: 14, borderTop: '1px dashed rgba(0,0,0,0.1)', paddingTop: 12 }}>
                  {!showNewField ? (
                    <button onClick={() => setShowNewField(true)} style={{ width: '100%', padding: '9px 12px', background: 'rgba(124,58,237,0.06)', border: '1px dashed rgba(124,58,237,0.3)', borderRadius: 9, cursor: 'pointer', fontSize: 13, color: '#7c3aed', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                      <span style={{ fontSize: 16 }}>+</span> Create New Field
                    </button>
                  ) : (
                    <div style={{ background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>New Custom Field</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input className="crm-input" placeholder="Field label (e.g. School Name)" value={newField.label} onChange={e => setNewField(p => ({ ...p, label: e.target.value }))} style={{ fontSize: 12, height: 32 }} />
                        <select className="crm-input" value={newField.type} onChange={e => setNewField(p => ({ ...p, type: e.target.value }))} style={{ fontSize: 12, height: 32 }}>
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="email">Email</option>
                          <option value="tel">Phone</option>
                          <option value="date">Date</option>
                          <option value="select">Dropdown</option>
                          <option value="textarea">Long Text</option>
                        </select>
                        {newField.type === 'select' && (
                          <input className="crm-input" placeholder="Options (comma-separated)" value={newField.options} onChange={e => setNewField(p => ({ ...p, options: e.target.value }))} style={{ fontSize: 12, height: 32 }} />
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={handleCreateField} disabled={savingNewField || !newField.label.trim()} style={{ flex: 1, height: 32, borderRadius: 7, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newField.label.trim() ? 'pointer' : 'not-allowed', opacity: newField.label.trim() ? 1 : 0.5 }}>
                            {savingNewField ? 'Saving…' : 'Add Field'}
                          </button>
                          <button onClick={() => { setShowNewField(false); setNewField({ label: '', type: 'text', options: '' }); }} style={{ height: 32, padding: '0 12px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.12)', background: '#fff', fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>This field will also be saved to your organisation's field list.</div>
                      </div>
                    </div>
                  )}
                </div>

                {AVAILABLE_FIELDS.filter(af => !form.fields.find(f => f.key === af.key)).length === 0
                  && (customFields || []).filter(cf => !form.fields.find(f => f.key === 'cf_' + cf.id)).length === 0 && (
                  <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>All fields added ✓</div>
                )}
              </div>
            </div>
          )}

          {/* ── Messages tab ── */}
          {tab === 'messages' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Thank You Heading</label>
                <input className="crm-input" value={form.thankYouTitle} onChange={e => set('thankYouTitle', e.target.value)} placeholder="Thank You!" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Thank You Message</label>
                <textarea className="crm-input" value={form.thankYouMsg} onChange={e => set('thankYouMsg', e.target.value)} rows={3} style={{ resize: 'vertical' }} placeholder="We'll get back to you shortly…" />
              </div>
              <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 6 }}>Preview</div>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{form.thankYouTitle || 'Thank You!'}</div>
                  <div style={{ fontSize: 13, color: '#475569', maxWidth: 320, margin: '0 auto', lineHeight: 1.5 }}>{form.thankYouMsg || '…'}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Advanced tab ── */}
          {tab === 'advanced' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              <div className="glass-card" style={{ padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>🔗 Redirect After Submit</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Optionally redirect to a URL after the form is submitted (instead of showing the thank-you screen).</div>
                <input className="crm-input" value={form.redirectUrl} onChange={e => set('redirectUrl', e.target.value)} placeholder="https://yoursite.com/thank-you" style={{ width: '100%' }} />
              </div>

              <div className="glass-card" style={{ padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>🔒 Privacy Notice</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Shown below the submit button. Link to your privacy policy.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>Privacy Text</label>
                    <input className="crm-input" value={form.privacyText} onChange={e => set('privacyText', e.target.value)} placeholder="By submitting you agree to our privacy policy." style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>Privacy Policy URL <span style={{ color: '#94a3b8' }}>(optional — makes the text a link)</span></label>
                    <input className="crm-input" value={form.privacyUrl} onChange={e => set('privacyUrl', e.target.value)} placeholder="https://yoursite.com/privacy" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>

              <div className="glass-card" style={{ padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>👁️ Form Visibility</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Control who can see and use this form in the CRM.</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { val: 'team',    icon: '👥', label: 'Team',    desc: 'All team members can see and use this form' },
                    { val: 'private', icon: '🔒', label: 'Private', desc: 'Only you (and admins) can see this form' },
                  ].map(opt => (
                    <div key={opt.val} onClick={() => set('visibility', opt.val)} style={{ flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: '2px solid ' + (form.visibility === opt.val ? '#7c3aed' : 'rgba(0,0,0,0.08)'), background: form.visibility === opt.val ? 'rgba(124,58,237,0.06)' : 'transparent', transition: 'all 0.15s' }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card" style={{ padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>💬 WhatsApp Opt-in</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Add an opt-in checkbox so leads consent to receive WhatsApp messages from you.</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div onClick={() => set('whatsappOptIn', !form.whatsappOptIn)} style={{ width: 38, height: 21, borderRadius: 11, cursor: 'pointer', background: form.whatsappOptIn ? '#25d366' : 'rgba(0,0,0,0.1)', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 15, height: 15, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.whatsappOptIn ? 20 : 3, transition: 'left 0.2s' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>Show WhatsApp opt-in checkbox</span>
                </div>
                {form.whatsappOptIn && (
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>Checkbox Label</label>
                    <input className="crm-input" value={form.whatsappOptInText} onChange={e => set('whatsappOptInText', e.target.value)} style={{ width: '100%' }} />
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: 20 }}>
          {saveError && (
            <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 9, fontSize: 12, color: '#dc2626', lineHeight: 1.5 }}>
              ⚠️ {saveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : editForm ? 'Save Changes' : 'Create Form'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sharing Modal ────────────────────────────────────────────────────────────
function SharingModal({ form, onClose }) {
  const [copied, setCopied] = useState('');
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };
  const url   = getPublicURL(form.id);
  const embed = getEmbedCode(form.id);
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Share "{form.name}"</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>🔗 Direct Link</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="crm-input" readOnly value={url} style={{ flex: 1, fontSize: 12, color: '#475569' }} />
            <button onClick={() => copy(url, 'link')} className="btn-primary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
              {copied === 'link' ? '✓ Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>🖼️ Embed Code</div>
          <textarea readOnly value={embed} style={{ width: '100%', minHeight: 80, fontFamily: 'monospace', fontSize: 11, padding: '10px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(0,0,0,0.03)', resize: 'vertical', color: '#475569' }} />
          <button onClick={() => copy(embed, 'embed')} style={{ marginTop: 8, padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 12 }}>
            {copied === 'embed' ? '✓ Copied!' : 'Copy Embed Code'}
          </button>
        </div>

        <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', marginBottom: 8 }}>How to use</div>
          {[
            ['🔗 Share the link', 'Post it on WhatsApp, Instagram bio, or your email signature'],
            ['🖼️ Embed on website', 'Paste the iframe code into any webpage or landing page'],
            ['📱 QR Code', 'Generate a QR code from the link above for print materials'],
          ].map(([t, d]) => (
            <div key={t} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', minWidth: 140 }}>{t}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main WebForms Page ────────────────────────────────────────────────────────
// ─── Reusable Image Upload Component ──────────────────────────────────────────
function ImageUpload({ value, onChange, folder = 'uploads' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState('');
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024)    { setError('Image must be under 5 MB'); return; }
    setError('');
    setUploading(true);
    setProgress(0);
    const path = folder + '/' + Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      err  => { setError('Upload failed: ' + err.message); setUploading(false); },
      ()   => { getDownloadURL(task.snapshot.ref).then(url => { onChange(url); setUploading(false); }); }
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {value && (
          <img src={value} alt="preview"
            style={{ height: 40, maxWidth: 140, objectFit: 'contain', borderRadius: 7,
              border: '1px solid rgba(0,0,0,0.08)', background: '#f8fafc', padding: 2 }} />
        )}
        <button type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ padding: '7px 14px', borderRadius: 9, border: '1.5px dashed rgba(124,58,237,0.35)',
            background: 'rgba(124,58,237,0.04)', color: '#7c3aed', cursor: uploading ? 'default' : 'pointer',
            fontSize: 12, fontWeight: 600, transition: 'all 0.15s' }}>
          {uploading ? 'Uploading ' + progress + '%…' : value ? '🔄 Replace Image' : '📁 Upload Image'}
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')}
            style={{ padding: '7px 10px', borderRadius: 9, border: '1px solid rgba(239,68,68,0.2)',
              background: 'rgba(239,68,68,0.05)', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
            Remove
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
          style={{ display: 'none' }} />
      </div>
      {uploading && (
        <div style={{ marginTop: 6, height: 4, background: 'rgba(0,0,0,0.06)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: progress + '%', background: '#7c3aed', borderRadius: 2, transition: 'width 0.3s' }} />
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

export default function WebForms() {
  const { webForms, deleteWebForm, currentUser, updateWebForm } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // Counsellors see: team forms + their own private forms
  const visibleForms = isAdmin ? webForms : webForms.filter(f =>
    f.visibility !== 'private' || f.createdById === currentUser?.id
  );
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [sharingForm, setSharingForm] = useState(null);

  const openEdit = (form) => { setEditingForm(form); setShowBuilder(true); };
  const openNew  = () => { setEditingForm(null); setShowBuilder(true); };

  return (
    <div className="page-enter" style={{ padding: '24px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Web Forms</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>Create embeddable lead capture forms for your website, social media, or WhatsApp.</p>
        </div>
        <button className="btn-primary" onClick={openNew}>+ Create Form</button>
      </div>

      {/* Empty state */}
      {visibleForms.length === 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, marginBottom: 32 }}>
          {[
            { icon: '🔗', title: 'Get a shareable link', desc: 'Copy the link or embed code for any page on your site' },
            { icon: '🎨', title: 'Customise branding',   desc: 'Set your logo, colours and messaging to match your brand' },
            { icon: '📥', title: 'Auto-import leads',    desc: 'Form submissions land directly in your CRM lead pipeline' },
            { icon: '📊', title: 'Track the source',     desc: 'Each form has a source tag so you know where leads came from' },
          ].map(f => (
            <div key={f.title} className="glass-card" style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      )}

      {visibleForms.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>No forms yet</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Create your first form and start capturing leads automatically.</div>
          <button className="btn-primary" onClick={openNew}>+ Create Your First Form</button>
        </div>
      )}

      {/* Forms grid */}
      {visibleForms.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 16 }}>
          {visibleForms.map(form => (
            <div key={form.id} className="glass-card" style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                {form.logoUrl
                  ? <img src={form.logoUrl} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, border: '1px solid rgba(0,0,0,0.07)', flexShrink: 0 }} />
                  : <div style={{ width: 40, height: 40, borderRadius: 10, background: (form.accentColor || '#7c3aed') + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📋</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{form.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.title}</div>
                </div>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: form.accentColor || '#7c3aed', flexShrink: 0, marginTop: 4 }} />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ padding: '3px 9px', borderRadius: 6, background: 'rgba(124,58,237,0.1)', color: '#7c3aed', fontSize: 11, fontWeight: 500 }}>
                  {(form.fields || []).filter(f => f.enabled).length} fields
                </span>
                <span style={{ padding: '3px 9px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', color: '#10b981', fontSize: 11, fontWeight: 500 }}>
                  {form.source}
                </span>
                {form.whatsappOptIn && (
                  <span style={{ padding: '3px 9px', borderRadius: 6, background: 'rgba(37,211,102,0.1)', color: '#25d366', fontSize: 11, fontWeight: 500 }}>💬 WA opt-in</span>
                )}
              </div>

              <div style={{ background: 'rgba(0,0,0,0.04)', borderRadius: 7, padding: '6px 10px', fontSize: 10.5, color: '#64748b', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                🔗 {getPublicURL(form.id)}
              </div>

              <div style={{ display: 'flex', gap: 7, marginTop: 'auto' }}>
                <button onClick={() => setSharingForm(form)} style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.07)', color: '#3b82f6', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>🔗 Share</button>
                <button onClick={() => openEdit(form)} style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', color: '#475569', fontSize: 12, cursor: 'pointer' }}>✏️ Edit</button>
                <button onClick={() => { if (window.confirm("Delete this form?")) deleteWebForm(form.id); }} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {visibleForms.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No web forms yet</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Create your first lead capture form to embed on your website</div>
          <button onClick={() => setShowBuilder(true)} className="btn-primary">+ Create Web Form</button>
        </div>
      )}

      {/* Sharing Modal */}
      {sharingForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Share Form — {sharingForm.name}</h3>
              <button onClick={() => setSharingForm(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Public Link</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input readOnly value={getPublicURL(sharingForm.id)} className="crm-input" style={{ flex: 1, fontSize: 12 }} />
                <button onClick={() => navigator.clipboard?.writeText(getPublicURL(sharingForm.id))}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.08)', color: '#7c3aed', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                  Copy
                </button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Embed Code</div>
              <textarea readOnly rows={4} className="crm-input"
                value={`<iframe src="${getPublicURL(sharingForm.id)}" width="100%" height="600" frameborder="0" style="border-radius:12px;"></iframe>`}
                style={{ width: '100%', fontSize: 11, fontFamily: 'monospace', resize: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Form Builder Modal */}
      {showBuilder && (
        <FormBuilderModal
          editForm={editingForm}
          onClose={() => { setShowBuilder(false); setEditingForm(null); }}
        />
      )}
    </div>
  );
}
